import { createSelector } from "@reduxjs/toolkit";
import { PostMcbimItemsApiResponse, openapi } from "../../openapi";
import { IRootState } from "../app/store";
import { selectProjectNo } from "../globalSelectors";

const selectActiveDisciplinesSorted = createSelector(
  [(s: IRootState) => s.design.disciplinesToDisplay],
  (disciplinesToDisplay) => {
    const active = Object.entries(disciplinesToDisplay ?? {})
      .filter(([, isOn]) => isOn === true)
      .map(([k]) => k);

    active.sort((a, b) => String(a).localeCompare(String(b)));
    return active;
  }
);

const buildArgsForDiscipline = (
  projectNo: number,
  buildingStoryName: string,
  discipline: string
) => {
  return {
    body: {
      projectNo,
      buildingStoryName,
      // Viktig: backend krever instanceClasses i input i dagens versjon.
      // Hvis dere har gjort den optional når disciplines finnes, kan dere fjerne dette.
      disciplines: [discipline],
    },
  };
};

/**
 * Slår ihop alle RTKQ-cachade svar (en per aktiv disiplin) til én liste for vieweren.
 * Viktig: Läsning sker från RTK Query state (mcbimApi) via endpoint.select(args).
 */
const selectFloorAndJob = (state: IRootState) => state.design.floorAndJob;
export const selectMcBimItemsWithDisciplinesAsArgsWithLoadingInfoForViewer = createSelector(
  [selectProjectNo, selectFloorAndJob, selectActiveDisciplinesSorted, (s: IRootState) => s],
  (projectNo, floorAndJob, activeDisciplines, state) => {
    const buildingStoryName = floorAndJob?.floorName;

    if (!projectNo || !buildingStoryName || activeDisciplines.length === 0) {
      return {
        items: [] as PostMcbimItemsApiResponse[number][],
        isLoading: false,
        isFetching: false,
        errorsByDiscipline: {} as Partial<Record<string, unknown>>,
      };
    }

    const items: PostMcbimItemsApiResponse[number][] = [];
    let isLoading = false;
    let isFetching = false;
    const errorsByDiscipline: Partial<Record<string, unknown>> = {};

    for (const discipline of activeDisciplines) {
      const args = buildArgsForDiscipline(projectNo, buildingStoryName, discipline);

      const q = openapi.endpoints.postMcbimItems.select(args)(state);

      if (q.isLoading) isLoading = true;

      if (q.error) errorsByDiscipline[discipline] = q.error;

      const data = q.data as PostMcbimItemsApiResponse | undefined;
      if (data?.length) {
        items.push(...data);
      }
    }

    return {
      items,
      isLoading,
      isFetching,
      errorsByDiscipline,
    };
  }
);
